import type { Moment } from 'moment'

import type {
  PatchThingToDoItemViewModel,
  PatchThingToDoItemRequest,
  PatchRestaurantBarItemViewModel,
  PatchRestaurantBarItemRequest,
  PatchCarRentalItemRequest,
  PatchCarRentalItemViewModel,
  PatchOtherTransportItemViewModel,
  PatchOtherTransportItemRequest,
  PatchFerryItemViewModel,
  PatchFerryItemRequest,
  PatchTrainItemViewModel,
  PatchTrainItemRequest,
  PatchBusItemRequest,
  PatchBusItemViewModel,
  PatchNoteItemRequest,
  PatchNoteItemViewModel,
  PatchEventItemViewModel,
  PatchEventItemRequest,
  PatchAttractionItemViewModel,
  PatchAttractionItemRequest,
  PatchCruiseItemViewModel,
  PatchCruiseItemRequest,
  PatchTourItemViewModel,
  PatchTourItemRequest,
  PatchFlightItemViewModel,
  PatchFlightItemRequest,
  PatchAccommodationItemViewModel,
  PatchAccommodationItemRequest,
} from './models'

import {
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
} from 'constants/dateFormats'
import type { Place } from 'tripPlanner/types/common'
import type { TripItem } from 'tripPlanner/types/tripItem'

export function extractEditableStartDateTimeStrings<
  I extends {
    startDate: Moment | undefined
    startTime: Moment | undefined
  },
>(item: I): { startTime: string | undefined, startDate: string | undefined } {
  const dateTimeStrings: {
    startTime: string | undefined
    startDate: string | undefined
  } = {
    startTime: undefined,
    startDate: undefined,
  }

  if (item.startTime) {
    dateTimeStrings.startTime = item.startTime.format(SHORT_TIME_FORMAT_24_HOUR)
  }

  if (item.startDate) {
    dateTimeStrings.startDate = item.startDate.format(ISO_DATE_FORMAT)
  }

  return dateTimeStrings
}

export function extractEditableEndDateTimeStrings<
  I extends {
    endDate: Moment | undefined
    endTime: Moment | undefined
  },
>(item: I): { endTime: string | undefined, endDate: string | undefined } {
  const dateTimeStrings: {
    endTime: string | undefined
    endDate: string | undefined
  } = {
    endTime: undefined,
    endDate: undefined,
  }

  if (item.endTime) {
    dateTimeStrings.endTime = item.endTime.format(SHORT_TIME_FORMAT_24_HOUR)
  }

  if (item.endDate) {
    dateTimeStrings.endDate = item.endDate.format(ISO_DATE_FORMAT)
  }

  return dateTimeStrings
}

export function isEndDateSame(
  item: Extract<TripItem, { endDate: Moment | undefined }>,
) {
  return (
    !!item.startDate &&
    !!item.endDate &&
    item.startDate.toISOString() === item.endDate.toISOString()
  )
}

export function isEndPlaceSame(item: Extract<TripItem, { endPlace: Place }>) {
  return (
    'id' in item.startPlace &&
    'id' in item.endPlace &&
    item.startPlace.id === item.endPlace.id
  )
}

export const updateFlightPayloadFromViewModel = (
  viewModel: PatchFlightItemViewModel,
): PatchFlightItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateAccommodationPayloadFromViewModel = (
  viewModel: PatchAccommodationItemViewModel,
): PatchAccommodationItemRequest => {
  const { ...rest } = viewModel

  return rest
}

export const updateTourPayloadFromViewModel = (
  viewModel: PatchTourItemViewModel,
): PatchTourItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return rest
}

export const updateCruisePayloadFromViewModel = (
  viewModel: PatchCruiseItemViewModel,
): PatchCruiseItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return rest
}

export const updateAttractionPayloadFromViewModel = (
  viewModel: PatchAttractionItemViewModel,
): PatchAttractionItemRequest => {
  const { ...rest } = viewModel

  return rest
}

export const updateEventPayloadFromViewModel = (
  viewModel: PatchEventItemViewModel,
): PatchEventItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return {
    ...rest,
    // End date and time are removed from event type
    endDate: null,
    endTime: null,
  }
}

export const updateThingToDoPayloadFromViewModel = (
  viewModel: PatchThingToDoItemViewModel,
): PatchThingToDoItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return {
    ...rest,
    // End date, time and place are removed from thing-to-do type
    endDate: null,
    endTime: null,
    endPlace: rest.startPlace,
  }
}

export const updateNotePayloadFromViewModel = (
  viewModel: PatchNoteItemViewModel,
): PatchNoteItemRequest => {
  const { ...rest } = viewModel

  return rest
}

export const updateBusPayloadFromViewModel = (
  viewModel: PatchBusItemViewModel,
): PatchBusItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateTrainPayloadFromViewModel = (
  viewModel: PatchTrainItemViewModel,
): PatchTrainItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateFerryPayloadFromViewModel = (
  viewModel: PatchFerryItemViewModel,
): PatchFerryItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateOtherTransportPayloadFromViewModel = (
  viewModel: PatchOtherTransportItemViewModel,
): PatchOtherTransportItemRequest => {
  const { isEndDateSame, ...rest } = viewModel

  return rest
}

export const updateCarRentalPayloadFromViewModel = (
  viewModel: PatchCarRentalItemViewModel,
): PatchCarRentalItemRequest => {
  const { isEndDateSame, isEndPlaceSame, ...rest } = viewModel

  return rest
}

export const updateRestaurantBarPayloadFromViewModel = (
  viewModel: PatchRestaurantBarItemViewModel,
): PatchRestaurantBarItemRequest => {
  const { ...rest } = viewModel

  return rest
}
